<template>
  <v-container fluid class="my-5">
    <div class="d-flex justify-space-between align-center flex-wrap">
      <div>
        <h2 class="subheading grey--text">Empleados de Oficina</h2>
      </div>
      <v-btn color="primary" @click="openNewUserDialog()">
        <v-icon left>mdi-plus</v-icon>
        Nuevo empleado
      </v-btn>
    </div>

    <v-row>
      <v-col cols="12" sm="6" md="3" v-for="card in summaryCards" :key="card.label">
        <v-card class="elevation-2 stat-card">
          <v-card-text>
            <v-icon :color="card.color" class="mb-2" large>{{ card.icon }}</v-icon>
            <div class="stat-value">{{ card.value }}</div>
            <div class="stat-label grey--text">{{ card.label }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="elevation-2 mt-2">
      <v-data-table
        :headers="headers"
        :items="tableItems"
        :footer-props="{
          itemsPerPageOptions: [{text: '5', value: 5}, {text: '10', value: 10}, {text: 'Todos', value: -1}],
          itemsPerPageText: 'Filas por página'
        }"
        :no-data-text="'No hay empleados registrados'"
        class="elevation-0"
      >
        <template v-slot:item.fullName="{ item }">
          <strong>{{ item.fullName }}</strong>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip small :color="item.status === 'active' ? 'green' : 'red'" text-color="white">{{ item.status === 'active' ? 'Activo' : 'Inactivo' }}</v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" v-on="on" @click="editUser(item)">mdi-pencil</v-icon>
            </template>
            <span>Editar</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small v-on="on" @click="toggleUserStatus(item)">mdi-toggle-switch-outline</v-icon>
            </template>
            <span>Activar / desactivar</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialogNewUser" persistent scrollable max-width="620">
      <v-form @submit.prevent="saveUser()">
        <v-card class="elevation-3 rounded-lg">
          <v-toolbar dark color="primary" flat dense>
            <v-icon left>{{ editing ? 'mdi-account-edit' : 'mdi-account-plus' }}</v-icon>
            <v-toolbar-title class="subtitle-1 font-weight-medium">{{ editing ? 'Editar empleado' : 'Nuevo empleado' }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon small @click="dialogNewUser = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-tabs v-model="userTab" grow color="primary" show-arrows>
            <v-tab><v-icon left small>mdi-card-account-details-outline</v-icon>Datos</v-tab>
            <v-tab><v-icon left small>mdi-image-multiple-outline</v-icon>Fotografías</v-tab>
            <v-tab><v-icon left small>mdi-shield-key-outline</v-icon>Permisos</v-tab>
          </v-tabs>
          <v-divider></v-divider>
          <v-tabs-items v-model="userTab">
            <v-tab-item>
              <v-card flat>
                <v-card-text class="pb-0 pt-4">
                  <div class="section-label">Datos laborales</div>
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-select v-model="newUser.area" :items="areaOptions" item-text="text" item-value="value" label="Área" prepend-inner-icon="mdi-office-building-outline" outlined dense clearable></v-select>
                    </v-col>
                    <v-col cols="12" sm="6" v-if="editing">
                      <v-text-field :value="newUser.employee_number" label="Número de empleado" prepend-inner-icon="mdi-identifier" outlined dense readonly disabled></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field v-model="newUser.description" label="Descripción / Puesto" prepend-inner-icon="mdi-briefcase-outline" outlined dense></v-text-field>
                    </v-col>
                  </v-row>

                  <div class="section-label">Datos personales</div>
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="newUser.first_name" label="Nombre" prepend-inner-icon="mdi-account-outline" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="newUser.last_name" label="Apellido" prepend-inner-icon="mdi-account-outline" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field v-model="newUser.email" label="Correo" prepend-inner-icon="mdi-email-outline" outlined dense type="email"></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <vue-tel-input
                        v-model="newUser.cellNumber"
                        default-country="MX"
                        valid-characters-only
                        @onInput="onPhoneInput"
                      ></vue-tel-input>
                    </v-col>
                  </v-row>

                  <div class="section-label">{{ editing ? 'Restablecer contraseña' : 'Acceso a la plataforma' }}</div>
                  <p v-if="editing" class="caption grey--text mt-n2 mb-2">Déjalo en blanco para no cambiar la contraseña actual</p>
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="newUser.password" :type="showPassword ? 'text' : 'password'" :label="editing ? 'Nueva contraseña' : 'Contraseña'" prepend-inner-icon="mdi-lock-outline" :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" @click:append="showPassword = !showPassword" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-text-field v-model="newUser.passwordConfirmation" :type="showPasswordConfirmation ? 'text' : 'password'" label="Confirmar contraseña" prepend-inner-icon="mdi-lock-check-outline" :append-icon="showPasswordConfirmation ? 'mdi-eye-off' : 'mdi-eye'" @click:append="showPasswordConfirmation = !showPasswordConfirmation" outlined dense></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-card-text class="pt-0 pb-4">
                  <v-divider class="mb-3"></v-divider>
                  <div class="d-flex align-center justify-space-between">
                    <div>
                      <div class="body-2 font-weight-medium">Estado del empleado</div>
                      <div class="caption grey--text">{{ isActive ? 'El empleado tiene acceso activo' : 'El empleado está deshabilitado' }}</div>
                    </div>
                    <v-switch v-model="isActive" color="primary" hide-details inset></v-switch>
                  </div>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item>
              <v-card flat>
                <v-card-text class="pt-4">
                  <div class="d-flex flex-column align-center mb-4">
                    <input ref="avatarInput" class="d-none" type="file" accept="image/*" @change="loadImagePreview($event, 'avatar', 'avatarFile')">
                    <div class="avatar-upload" role="button" tabindex="0" @click="openImagePicker('avatarInput')" @keydown.enter.prevent="openImagePicker('avatarInput')" @keydown.space.prevent="openImagePicker('avatarInput')">
                      <v-avatar size="104" color="grey lighten-3">
                        <v-img v-if="newUser.avatar" :src="newUser.avatar" alt="Foto de perfil"></v-img>
                        <v-icon v-else size="48" color="grey darken-1">mdi-account</v-icon>
                      </v-avatar>
                      <v-avatar size="32" color="primary" class="avatar-upload-badge">
                        <v-icon size="18" color="white">mdi-camera</v-icon>
                      </v-avatar>
                    </div>
                    <div class="caption grey--text mt-2">{{ editing ? 'Foto de perfil' : 'Foto de perfil *' }}</div>
                  </div>
                  <div class="section-label">Identificación oficial (INE){{ !editing ? ' *' : '' }}</div>
                  <p v-if="!editing" class="caption grey--text mt-n2 mb-2">La foto de perfil y el INE son obligatorios para crear al empleado</p>
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <input ref="ineFrontInput" class="d-none" type="file" accept="image/*" @change="loadImagePreview($event, 'idFront', 'idFrontFile')">
                      <div class="image-upload-zone" role="button" tabindex="0" @click="openImagePicker('ineFrontInput')" @keydown.enter.prevent="openImagePicker('ineFrontInput')" @keydown.space.prevent="openImagePicker('ineFrontInput')">
                        <img v-if="newUser.idFront" :src="newUser.idFront" alt="Vista previa de INE frente" class="image-upload-preview">
                        <div v-else class="image-upload-placeholder">
                          <v-icon color="primary">mdi-card-account-details-outline</v-icon>
                          <span>INE frente</span>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <input ref="ineBackInput" class="d-none" type="file" accept="image/*" @change="loadImagePreview($event, 'ineBack', 'ineBackFile')">
                      <div class="image-upload-zone" role="button" tabindex="0" @click="openImagePicker('ineBackInput')" @keydown.enter.prevent="openImagePicker('ineBackInput')" @keydown.space.prevent="openImagePicker('ineBackInput')">
                        <img v-if="newUser.ineBack" :src="newUser.ineBack" alt="Vista previa de INE atrás" class="image-upload-preview">
                        <div v-else class="image-upload-placeholder">
                          <v-icon color="primary">mdi-card-account-details-outline</v-icon>
                          <span>INE atrás</span>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item>
              <v-card flat>
                <v-card-text class="pt-4" v-if="editing">
                  <div class="section-label mb-2">Permisos de gestión</div>
                  <v-list dense class="permission-list">
                    <v-list-item v-for="permission in availablePermissions" :key="permission.value" class="permission-item">
                      <v-list-item-icon class="mr-3">
                        <v-icon color="primary">{{ permission.icon }}</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ permission.label }}</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-switch v-model="newUser.selectedPermissions" :value="permission.value" color="primary" hide-details dense></v-switch>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-text v-else class="pt-4">
                  <v-alert color="accent" text dense icon="mdi-information-outline" border="left">
                    Los permisos se asignan una vez creado el empleado. Guarda al empleado y edítalo para configurar sus permisos.
                  </v-alert>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
          <v-divider></v-divider>
          <v-card-actions class="px-4 py-3">
            <v-btn color="grey darken-1" text :disabled="loading" @click="dialogNewUser = false">Cancelar</v-btn>
            <v-spacer></v-spacer>
            <v-btn v-if="!editing && userTab === 0" color="primary" depressed type="button" @click="goToNextTab">
              Siguiente
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
            <v-btn v-else color="primary" depressed type="submit" :loading="loading" :disabled="loading">
              <v-icon left small>{{ editing ? 'mdi-content-save-outline' : 'mdi-check' }}</v-icon>
              {{ editing ? 'Guardar cambios' : 'Crear empleado' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import employeeService, { extractApiErrorMessage } from '@/services/employeeService'

export default {
  name: 'oficina_usuarios',
  data () {
    return {
      users: [],
      areas: [],
      dialogNewUser: false,
      editing: false,
      editingEmployeeId: '',
      originalUser: null,
      userTab: 0,
      showPassword: false,
      showPasswordConfirmation: false,
      loading: false,
      newUser: this.emptyUser(),
      availablePermissions: [
        { value: 'invitations', label: 'Enviar invitaciones', icon: 'mdi-email-fast-outline' },
        { value: 'residents', label: 'Gestionar residentes', icon: 'mdi-account-group-outline' },
        { value: 'employees', label: 'Gestionar empleados y accesos', icon: 'mdi-badge-account-outline' },
        { value: 'areas', label: 'Gestionar áreas de oficina', icon: 'mdi-office-building-outline' },
        { value: 'fees', label: 'Gestionar cuotas y pagos', icon: 'mdi-cash-multiple' }
      ],
      headers: [
        { text: 'Nombre', value: 'fullName' },
        { text: 'Correo', value: 'email' },
        { text: 'Área', value: 'areaName' },
        { text: 'Estado', value: 'status' },
        { text: 'Acciones', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    ...mapGetters(['getProfile']),
    housingUnitId () {
      return this.getProfile.idHousingUnit
    },
    areaOptions () {
      return this.areas.map(area => ({ text: area.name, value: area.id }))
    },
    tableItems () {
      return this.users.map(u => {
        const userInfo = u.user || u.user_detail || {}
        const areaId = typeof u.area === 'string' ? u.area : (u.area && u.area.id)
        const areaFromList = this.areas.find(a => a.id === areaId)
        const areaName = (u.area && u.area.name) || (areaFromList && areaFromList.name) || '—'
        return {
          id: u.id,
          username: userInfo.username || userInfo.email || '',
          fullName: `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim(),
          email: userInfo.email || '',
          areaName,
          status: u.status,
          employee_number: u.employee_number,
          raw: u
        }
      })
    },
    isActive: {
      get () {
        return this.newUser.status === 'active'
      },
      set (value) {
        this.newUser.status = value ? 'active' : 'inactive'
      }
    },
    summaryCards () {
      const active = this.users.filter(u => u.status === 'active').length
      return [
        { label: 'Empleados activos', value: active, icon: 'mdi-account-check-outline', color: 'green' },
        { label: 'Total empleados', value: this.users.length, icon: 'mdi-account-multiple-outline', color: 'primary' }
      ]
    }
  },
  mounted () {
    this.loadData()
  },
  watch: {
    housingUnitId (newVal) {
      if (newVal) {
        this.loadData()
      }
    }
  },
  methods: {
    async loadData () {
      if (!this.housingUnitId) {
        console.error('Housing unit ID not available')
        return
      }
      this.loading = true
      try {
        const [areasData, usersData] = await Promise.all([
          employeeService.getAreas(this.housingUnitId),
          employeeService.getEmployees(this.housingUnitId)
        ])
        this.areas = areasData.results || areasData
        this.users = usersData.results || usersData
      } catch (error) {
        console.error('Error loading data:', error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', extractApiErrorMessage(error))
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    },
    emptyUser () {
      return {
        first_name: '',
        last_name: '',
        email: '',
        cellNumber: '',
        password: '',
        passwordConfirmation: '',
        area: '',
        employee_number: '',
        description: '',
        idFront: null,
        idFrontFile: null,
        ineBack: null,
        ineBackFile: null,
        avatar: null,
        avatarFile: null,
        status: 'active',
        selectedPermissions: []
      }
    },
    openNewUserDialog () {
      this.editing = false
      this.editingEmployeeId = ''
      this.userTab = 0
      this.showPassword = false
      this.showPasswordConfirmation = false
      this.newUser = this.emptyUser()
      this.dialogNewUser = true
    },
    editUser (tableItem) {
      const item = tableItem.raw
      const userInfo = item.user || item.user_detail || {}
      this.editing = true
      this.editingEmployeeId = item.id
      this.userTab = 0
      const currentPermissions = (item.permissions || []).map(p => p.permission)
      const areaId = typeof item.area === 'string' ? item.area : (item.area && item.area.id)
      this.newUser = {
        ...this.emptyUser(),
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        email: userInfo.email,
        cellNumber: userInfo.cellNumber,
        area: areaId || '',
        employee_number: item.employee_number,
        description: item.description,
        status: item.status,
        idFront: userInfo.idFront,
        ineBack: userInfo.idBack,
        avatar: userInfo.avatar,
        selectedPermissions: currentPermissions
      }
      this.originalUser = {
        email: userInfo.email,
        cellNumber: userInfo.cellNumber,
        first_name: userInfo.first_name,
        last_name: userInfo.last_name
      }
      this.dialogNewUser = true
    },
    openImagePicker (inputRef) {
      this.$refs[inputRef].click()
    },
    loadImagePreview (event, field, fileField) {
      const [file] = event.target.files
      if (!file) return
      this.newUser[fileField] = file
      const reader = new FileReader()
      reader.onload = () => {
        this.newUser[field] = reader.result
      }
      reader.readAsDataURL(file)
      event.target.value = ''
    },
    goToNextTab () {
      if (!this.newUser.first_name || !this.newUser.last_name || !this.newUser.email || !this.newUser.cellNumber) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', 'Completa nombre, apellido, correo y teléfono para continuar')
        this.$store.commit('snackbar_activate_true')
        return
      }
      if (!this.newUser.password) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', 'La contraseña es requerida para crear un empleado')
        this.$store.commit('snackbar_activate_true')
        return
      }
      if (this.newUser.password !== this.newUser.passwordConfirmation) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', 'Las contraseñas no coinciden')
        this.$store.commit('snackbar_activate_true')
        return
      }
      this.userTab = 1
    },
    buildUserChanges () {
      const changes = {}
      const original = this.originalUser || {}
      if (this.newUser.email && this.newUser.email !== original.email) changes.email = this.newUser.email
      if (this.newUser.cellNumber && this.newUser.cellNumber !== original.cellNumber) changes.cellNumber = this.newUser.cellNumber
      if (this.newUser.first_name && this.newUser.first_name !== original.first_name) changes.first_name = this.newUser.first_name
      if (this.newUser.last_name && this.newUser.last_name !== original.last_name) changes.last_name = this.newUser.last_name
      if (this.newUser.password) changes.password = this.newUser.password
      if (this.newUser.idFrontFile) changes.idFront = this.newUser.idFrontFile
      if (this.newUser.ineBackFile) changes.idBack = this.newUser.ineBackFile
      if (this.newUser.avatarFile) changes.avatar = this.newUser.avatarFile
      return changes
    },
    generateEmployeeNumber () {
      const numbers = this.users
        .map(u => u.employee_number)
        .filter(n => n && /^EMP\d+$/.test(n))
        .map(n => parseInt(n.replace('EMP', ''), 10))
      const next = numbers.length ? Math.max(...numbers) + 1 : 1
      return `EMP${String(next).padStart(5, '0')}`
    },
    async saveUser () {
      if (this.editing && this.newUser.password && this.newUser.password !== this.newUser.passwordConfirmation) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', 'Las contraseñas no coinciden')
        this.$store.commit('snackbar_activate_true')
        return
      }
      if (!this.editing && (!this.newUser.avatarFile || !this.newUser.idFrontFile || !this.newUser.ineBackFile)) {
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', 'La foto de perfil y el INE (frente y reverso) son obligatorios')
        this.$store.commit('snackbar_activate_true')
        this.userTab = 1
        return
      }

      try {
        this.loading = true
        const employeeData = {
          employee_number: this.editing ? this.newUser.employee_number : this.generateEmployeeNumber(),
          description: this.newUser.description,
          status: this.newUser.status,
          area: this.newUser.area || null,
          user: this.editing ? this.buildUserChanges() : {
            email: this.newUser.email,
            first_name: this.newUser.first_name,
            last_name: this.newUser.last_name,
            cellNumber: this.newUser.cellNumber,
            password: this.newUser.password,
            idFront: this.newUser.idFrontFile,
            idBack: this.newUser.ineBackFile,
            avatar: this.newUser.avatarFile
          }
        }

        let response
        if (this.editing) {
          response = await employeeService.updateEmployee(this.editingEmployeeId, employeeData)
          await this.updatePermissions()
          this.$store.commit('snackbar_change_message', `Empleado ${response.employee_number || ''} actualizado correctamente`.trim())
        } else {
          response = await employeeService.createEmployee(this.housingUnitId, employeeData)
          this.$store.commit('snackbar_change_message', `Empleado ${response.employee_number || ''} creado correctamente`.trim())
        }

        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.dialogNewUser = false
        await this.loadData()
      } catch (error) {
        console.error('Error saving employee:', error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', extractApiErrorMessage(error))
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    },
    async updatePermissions () {
      try {
        const currentEmployee = this.users.find(u => u.id === this.editingEmployeeId)
        const currentPermissions = ((currentEmployee && currentEmployee.permissions) || []).map(p => p.permission)
        const selectedPermissions = this.newUser.selectedPermissions || []

        const permissionsToRevoke = currentPermissions.filter(p => !selectedPermissions.includes(p))
        const permissionsToGrant = selectedPermissions.filter(p => !currentPermissions.includes(p))

        for (const permission of permissionsToRevoke) {
          await employeeService.revokePermission(this.editingEmployeeId, permission)
        }

        for (const permission of permissionsToGrant) {
          await employeeService.grantPermission(this.editingEmployeeId, permission)
        }
      } catch (error) {
        console.error('Error updating permissions:', error)
      }
    },
    async toggleUserStatus (tableItem) {
      const item = tableItem.raw
      try {
        this.loading = true
        const newStatus = item.status === 'active' ? 'inactive' : 'active'
        const response = await employeeService.updateEmployee(item.id, { status: newStatus })
        item.status = response.status || newStatus
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_change_message', `Empleado ${response.employee_number || ''} ahora está ${item.status === 'active' ? 'activo' : 'inactivo'}`.trim())
        this.$store.commit('snackbar_activate_true')
      } catch (error) {
        console.error('Error updating status:', error)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_change_message', extractApiErrorMessage(error))
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.stat-card {
  text-align: center;
  height: 100%;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.stat-label {
  font-size: 0.85rem;
}

.section-label {
  color: var(--v-primary-base);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  margin: 4px 0 8px;
  text-transform: uppercase;
}

.image-upload-zone {
  align-items: center;
  background-color: #fafafa;
  border: 1.5px dashed #bdbdbd;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  height: 120px;
  justify-content: center;
  overflow: hidden;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.image-upload-zone:hover,
.image-upload-zone:focus {
  background-color: #f1f8e9;
  border-color: var(--v-primary-base);
  outline: none;
}

.image-upload-placeholder {
  align-items: center;
  color: #9e9e9e;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.8rem;
}

.image-upload-preview {
  height: 100%;
  object-fit: contain;
  width: 100%;
}

.avatar-upload {
  cursor: pointer;
  position: relative;
  display: inline-block;
}

.avatar-upload-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  border: 2px solid white;
}

.permission-list {
  background: transparent;
}

.permission-item {
  border: 1px solid #ececec;
  border-radius: 8px;
  margin-bottom: 8px;
}
</style>
